import { useState, type ReactNode } from 'react';
import {
  Box,
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';

import {
  useAdminListLoanApplicationsQuery,
  useAdminLoanApplicationSummaryQuery,
} from '@/apis/extended/loanApplicationApi';
import { TastePageHeader } from '@/components/Common/TastePageHeader';
import { TasteSectionCard } from '@/components/Common/TasteSectionCard';
import type {
  LoanApplication,
  LoanBorrower,
} from '@/types/LoanApplicationType';

const TABLE_COL_COUNT = 11;

function checklistPct(r: LoanApplication): string {
  const items = r.checklist || [];
  if (items.length === 0) return '—';
  const required = items.filter(
    (i) => i.requiredForSubmit !== false && i.required !== false
  );
  const pool = required.length > 0 ? required : items;
  const done = pool.filter((i) => i.completed).length;
  return `${done}/${pool.length}`;
}

function assigneeLabel(r: LoanApplication): string {
  if (!r.assignedToAdminId) return 'Unassigned';
  return String(r.assignedToAdminId).slice(-6);
}

function primaryBorrower(r: LoanApplication): LoanBorrower | undefined {
  return r.borrowers?.find((b) => b.role === 'primary') ?? r.borrowers?.[0];
}

function pipelineApplicantName(r: LoanApplication): string {
  const b = primaryBorrower(r);
  const fromBorrower = [b?.firstName, b?.lastName]
    .filter(Boolean)
    .join(' ')
    .trim();
  if (fromBorrower) return fromBorrower;
  const fromUser = r.applicantUser?.name?.trim();
  if (fromUser) return fromUser;
  return '—';
}

function pipelineApplicantEmail(r: LoanApplication): string {
  const b = primaryBorrower(r);
  const email = b?.email?.trim() || r.applicantUser?.email?.trim();
  return email || '—';
}

function sourceProductCell(ref: LoanApplication['sourceProductId']): ReactNode {
  if (!ref) return '—';
  const id = typeof ref === 'string' ? ref : ref._id;
  const label =
    typeof ref === 'string'
      ? `Product ${id.slice(-8)}`
      : ref.name || ref.productId || id;
  return (
    <Link
      to={`/admin/product/${id}`}
      onClick={(e) => e.stopPropagation()}
      style={{ fontWeight: 500 }}
    >
      {label}
    </Link>
  );
}

const AdminLoanPipelinePage = () => {
  const navigate = useNavigate();
  const [status, setStatus] = useState('');
  const [productType, setProductType] = useState('');
  const [lifecycleStage, setLifecycleStage] = useState('');
  const { data: rows = [], isLoading } = useAdminListLoanApplicationsQuery({
    ...(status ? { status } : {}),
    ...(productType ? { productType } : {}),
    ...(lifecycleStage ? { lifecycleStage } : {}),
  });
  const { data: summary = [] } = useAdminLoanApplicationSummaryQuery();

  return (
    <Box>
      <TastePageHeader
        title="Loan pipeline"
        subtitle="Purchase files move here as offers ratify. Refinance files appear after a mortgage approval application is started. Complete checklist + application before underwriting submit."
      />
      {summary.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 1,
            mb: 2,
            alignItems: 'center',
          }}
        >
          <Typography variant="caption" color="text.secondary">
            Rollup:
          </Typography>
          {summary.slice(0, 16).map((row, i) => (
            <Chip
              key={i}
              size="small"
              variant="outlined"
              label={`${row._id.flowProfile ?? '—'} · ${
                row._id.lifecycleStage ?? '—'
              } · ${row._id.status}: ${row.count}`}
            />
          ))}
        </Box>
      )}
      <TasteSectionCard
        sx={{ p: 2, mb: 2, display: 'flex', gap: 2, flexWrap: 'wrap' }}
      >
        <TextField
          size="small"
          label="Status"
          select
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="draft">Draft</MenuItem>
          <MenuItem value="submitted">Submitted</MenuItem>
          <MenuItem value="in_review">In review</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="declined">Declined</MenuItem>
          <MenuItem value="withdrawn">Withdrawn</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Product"
          select
          value={productType}
          onChange={(e) => setProductType(e.target.value)}
          sx={{ minWidth: 140 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="dscr">DSCR</MenuItem>
          <MenuItem value="conventional">Conventional</MenuItem>
          <MenuItem value="fha">FHA</MenuItem>
          <MenuItem value="va">VA</MenuItem>
          <MenuItem value="usda">USDA</MenuItem>
          <MenuItem value="bridge">Bridge</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </TextField>
        <TextField
          size="small"
          label="Lifecycle"
          select
          value={lifecycleStage}
          onChange={(e) => setLifecycleStage(e.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="shopping">Shopping</MenuItem>
          <MenuItem value="under_contract">Under contract</MenuItem>
          <MenuItem value="ratified_purchase">Ratified purchase</MenuItem>
        </TextField>
      </TasteSectionCard>
      <TasteSectionCard>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Property / ID</TableCell>
              <TableCell>Product type</TableCell>
              <TableCell>Flow</TableCell>
              <TableCell>Lifecycle</TableCell>
              <TableCell>Checklist</TableCell>
              <TableCell>Assignee</TableCell>
              <TableCell>Source listing</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={TABLE_COL_COUNT}>Loading…</TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={TABLE_COL_COUNT}>
                  No applications match filters.
                </TableCell>
              </TableRow>
            ) : (
              rows.map((r) => (
                <TableRow
                  key={r._id}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/loan-pipeline/${r._id}`)}
                >
                  <TableCell>{pipelineApplicantName(r)}</TableCell>
                  <TableCell sx={{ maxWidth: 220 }}>
                    <Typography
                      variant="body2"
                      noWrap
                      title={pipelineApplicantEmail(r)}
                    >
                      {pipelineApplicantEmail(r)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {r.property?.subjectPropertyAddress || r._id}
                  </TableCell>
                  <TableCell>
                    <Chip label={r.productType?.toUpperCase()} size="small" />
                  </TableCell>
                  <TableCell>
                    {r.flowProfile ? r.flowProfile.replace(/_/g, ' ') : '—'}
                  </TableCell>
                  <TableCell>
                    {r.lifecycleStage
                      ? r.lifecycleStage.replace(/_/g, ' ')
                      : '—'}
                  </TableCell>
                  <TableCell>{checklistPct(r)}</TableCell>
                  <TableCell>{assigneeLabel(r)}</TableCell>
                  <TableCell>{sourceProductCell(r.sourceProductId)}</TableCell>
                  <TableCell>{r.status?.replace(/_/g, ' ')}</TableCell>
                  <TableCell>
                    {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : '—'}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TasteSectionCard>
    </Box>
  );
};

export { AdminLoanPipelinePage };
